import i18next from 'i18next'

import { notify } from './notify'

export type HttpErrorKey =
  | 'network'
  | 'server'
  | 'bad_request'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation'
  | 'rate_limit'
  | 'generic'

export interface HttpErrorInput {
  status?: number
  gqlCode?: string
  network?: boolean
}

const I18N_PREFIX = 'libs_sonner_error_'

const GQL_CODE_KEYS: { [code: string]: HttpErrorKey } = {
  FORBIDDEN: 'forbidden',
  BAD_USER_INPUT: 'validation',
  GRAPHQL_VALIDATION_FAILED: 'validation',
  INTERNAL_SERVER_ERROR: 'server',
}

const HTTP_STATUS_KEYS: { [status: number]: HttpErrorKey } = {
  400: 'bad_request',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  422: 'validation',
  429: 'rate_limit',
}

// 401 / UNAUTHENTICATED are intentionally silent: the auth flow handles them.
export const resolveErrorKey = ({ status, gqlCode, network }: HttpErrorInput): HttpErrorKey | null => {
  if (status === 401 || gqlCode === 'UNAUTHENTICATED') return null

  if (gqlCode) return GQL_CODE_KEYS[gqlCode] ?? 'generic'

  if (status !== undefined) {
    if (status >= 500) return 'server'
    return HTTP_STATUS_KEYS[status] ?? 'generic'
  }

  // Only an explicit fetch rejection is a connectivity issue; any other opaque error is generic.
  return network ? 'network' : 'generic'
}

export const notifyHttpError = (input: HttpErrorInput): void => {
  const key = resolveErrorKey(input)
  if (!key) return

  const i18nKey = `${I18N_PREFIX}${key}` as const
  // Reuse the i18n key as the toast id so concurrent identical failures collapse into one toast.
  // eslint-disable-next-line import/no-named-as-default-member
  notify.error(i18next.t(i18nKey), { id: i18nKey })
}
